/**
 * <kr-toc> — builds a table of contents from already-rendered rich text.
 *
 * Policy bodies come from a `richtext` setting or Shopify's `policy.body`, both of which are
 * opaque strings as far as Liquid is concerned — there is no way to enumerate their headings
 * server-side. So the list is assembled here, on the client, from the real DOM.
 *
 * Progressive enhancement throughout: the element ships `hidden` and only reveals itself once
 * it has found at least `MIN_HEADINGS` headings, so a short policy never renders a one-item
 * "contents" box, and a visitor without JS simply reads the policy.
 *
 * Attributes:
 *   data-target   — CSS selector for the rich-text container to scan (required)
 *   data-selector — heading selector, e.g. "h2" or "h2, h3" (defaults to "h2, h3")
 *
 * Refs:
 *   [data-kr-toc-list] — the <ol> the items are appended to
 */

const MIN_HEADINGS = 2;

class KrToc extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {Map<string, HTMLAnchorElement>} */
  #links = new Map();

  connectedCallback() {
    const target = document.querySelector(this.dataset.target ?? '');
    const list = this.querySelector('[data-kr-toc-list]');
    if (!(target instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const selector = this.dataset.selector || 'h2, h3';
    const headings = /** @type {HTMLElement[]} */ ([...target.querySelectorAll(selector)]).filter(
      (heading) => heading.textContent?.trim()
    );

    if (headings.length < MIN_HEADINGS) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const fragment = document.createDocumentFragment();
    const usedIds = new Set();

    for (const heading of headings) {
      const id = this.#ensureId(heading, usedIds);
      const item = document.createElement('li');
      item.className = `kr-policy__toc-item kr-policy__toc-item--${heading.tagName.toLowerCase()}`;

      const link = document.createElement('a');
      link.className = 'kr-policy__toc-link';
      link.href = `#${id}`;
      link.textContent = heading.textContent?.trim() ?? '';

      item.append(link);
      fragment.append(item);
      this.#links.set(id, link);
    }

    list.replaceChildren(fragment);
    this.hidden = false;

    this.addEventListener('click', this.#handleClick, { signal });
    this.#observeHeadings(headings);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#links.clear();
  }

  /**
   * Uses the heading's own id when the merchant set one, otherwise slugifies its text.
   * Falls back to an index suffix so two identically-named headings can't collide.
   *
   * @param {HTMLElement} heading
   * @param {Set<string>} usedIds
   * @returns {string}
   */
  #ensureId(heading, usedIds) {
    let id = heading.id;

    if (!id) {
      const base =
        heading.textContent
          ?.trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60) || 'section';

      id = base;
      let suffix = 2;
      while (usedIds.has(id) || document.getElementById(id)) {
        id = `${base}-${suffix++}`;
      }

      heading.id = id;
    }

    usedIds.add(id);
    return id;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const link = /** @type {HTMLElement} */ (event.target)?.closest?.('.kr-policy__toc-link');
    if (!(link instanceof HTMLAnchorElement)) return;

    const heading = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!heading) return;

    event.preventDefault();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    // Keep the URL shareable and move focus so keyboard users land on the section.
    history.replaceState(null, '', link.hash);
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  };

  /** @param {HTMLElement[]} headings */
  #observeHeadings(headings) {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#setActive(entry.target.id);
        }
      },
      // A band near the top of the viewport: whichever heading sits in it is "current".
      { rootMargin: '-10% 0px -80% 0px', threshold: 0 }
    );

    for (const heading of headings) this.#observer.observe(heading);
  }

  /** @param {string} id */
  #setActive(id) {
    for (const [linkId, link] of this.#links) {
      link.classList.toggle('kr-policy__toc-link--active', linkId === id);
    }
  }
}

if (!customElements.get('kr-toc')) {
  customElements.define('kr-toc', KrToc);
}
